import React, { useEffect, useState } from 'react';
import './TreeView.css';
import { FaChevronRight } from 'react-icons/fa';
import { LuChevronRight } from 'react-icons/lu';

const TreeNode = ({ node, onCheck, checkableIds = null, level = 0 }) => {
	const [expanded, setExpanded] = useState(false);
	const hasChildren = node.children && node.children.length > 0 && !node.hiddenChildren;

	const handleToggle = () => {
		setExpanded(!expanded);
	};

	const handleCheck = () => {
		onCheck(node.id, !node.checked);
	};

	return (
		<div className="tree-node">
			<div className={`node-content ${node.important ? 'node-important' : ''}`} style={{ paddingLeft: `26px` }}>
				{hasChildren && (
					<button
						type='button'
						className={`expand-button ${expanded ? 'expanded' : ''}`}
						onClick={handleToggle}
						aria-label={expanded ? 'Recolher' : 'Expandir'}
					>
						<span className="toggle-icon">
							<LuChevronRight className={`${expanded && 'rotate-90'} duration-200`} />
						</span>
					</button>
				)}
				<div className='inline-flex items-center justify-between flex-1'>
					<div className='inline-flex items-center gap-2'>
						{(!checkableIds || checkableIds.includes(node.id)) && (
							<input
								id={`checkbox-${node.id}`}
								type="checkbox"
								className="checkbox"
								checked={node.checked || false}
								onChange={handleCheck}
							/>
						)}
						<label htmlFor={`checkbox-${node.id}`} className="label">{node.name}</label>
					</div>
					{node.extraContent?.()}
				</div>
			</div>

			{hasChildren && expanded && (
				<div className="children">
					{node.children.map((child) => (
						<TreeNode
							key={child.id}
							node={child}
							onCheck={onCheck}
							checkableIds={checkableIds}
							level={level + 1}
						/>
					))}
				</div>
			)}
		</div>
	);
};

const TreeView = ({ data, selectedIds = [], checkableIds = null, onChange, singleCheck = false }) => {
	const [treeData, setTreeData] = useState(data);
	const [selected, setSelected] = useState(selectedIds ?? []);

	useEffect(() => {
		if (
			selectedIds === selected ||
			(selectedIds.length === selected.length && selectedIds.every(item => selected.includes(item)))
		) {
			return;
		}
		onChange?.(Array.from(selected));
	}, [selected]);

	useEffect(() => {
		setTreeData((prev) => updateAllNodesChecked({
			nodes: data,
			selectedIds
		}));
	}, [data]);

	useEffect(() => {
		setSelected(selectedIds ?? [])
	}, [selectedIds]);

	const findNode = (nodes, id) => {
		for (const node of nodes) {
			if (node.id === id) return node;
			if (node.children) {
				const found = findNode(node.children, id);
				if (found) return found;
			}
		}
		return null;
	};

	const updateNodeChecked = ({ nodes, id, checked, forceCheck = false, onlyTarget = false }) => {
		return nodes?.map((node) => {
			const isTargetNode = node.id === id;
			// const isAllChildsChecked = node.children?.every(node => node.checked);
			// const isChecking = node.checked === false && checked === true && isTargetNode;
			// const isUnchecking = node.checked === true && checked === false && isTargetNode && isAllChildsChecked;

			let updatedChildren = {};
			let _checked = false;

			if (onlyTarget) {
				updatedChildren = updateNodeChecked({
					nodes: node.children,
					id,
					checked,
					onlyTarget: true
				});
				_checked = isTargetNode && checked;
			} else {
				updatedChildren = updateNodeChecked({
					nodes: node.children,
					id,
					checked,
					// forceCheck: forceCheck || isChecking || isUnchecking
				});
				_checked = isTargetNode || forceCheck ? checked : node.checked;
			}

			if (_checked) {
				setSelected(selected => {
					const newSelected = new Set(selected);
					newSelected.add(node.id)
					return Array.from(newSelected);
				});
			} else if (selected.includes(node.id)) {
				setSelected(selected => {
					const newSelected = new Set(selected);
					newSelected.delete(node.id)
					return Array.from(newSelected);
				});
			}

			return {
				...node,
				checked: _checked,
				children: updatedChildren,
			};
		});
	};

	const updateAllNodesChecked = ({ nodes, selectedIds }) => {
		return nodes?.map((node) => {
			return {
				...node,
				checked: selectedIds.includes(node.id),
				children: updateAllNodesChecked({
					nodes: node.children,
					selectedIds,
				})
			};
		});
	};

	const handleCheck = (id, checked) => {
		setTreeData((prev) => updateNodeChecked({
			nodes: prev,
			id,
			checked,
			onlyTarget: singleCheck
		}));
	};

	return (
		<div className="tree-view">
			{treeData.map((node) => (
				<TreeNode key={node.id} checkableIds={checkableIds} node={node} onCheck={handleCheck} />
			))}
		</div>
	);
};

export default TreeView;